import React, { useLayoutEffect, useRef, useState } from 'react';
import { consultPrice } from '../config/wellwired';

export default function WellwiredInquiry({ includeBuildingBiologist = false, reviewVariant }) {
  const [status, setStatus] = useState('idle');
  const shellRef = useRef(null);
  const contentRef = useRef(null);
  const previousHeight = useRef(null);
  const updateStatus = next => {
    previousHeight.current = shellRef.current?.getBoundingClientRect().height;
    setStatus(next);
  };
  useLayoutEffect(() => {
    const shell = shellRef.current;
    const end = contentRef.current?.getBoundingClientRect().height;
    if (!shell?.animate || previousHeight.current == null || previousHeight.current === end || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const animation = shell.animate(
      [{ height: `${previousHeight.current}px` }, { height: `${end}px` }],
      { duration: 600, easing: 'cubic-bezier(0.45, 0, 0.25, 1)' },
    );
    return () => animation.cancel();
  }, [status]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    updateStatus('sending');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: new FormData(form),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error('Submission failed');
      updateStatus('sent');
      form.reset();
    } catch {
      updateStatus('error');
    }
  };

  const content = status === 'sent' ? (
    <div className="ww-inquiry-success" role="status">
      <h3>Thank you. Let’s find the fit.</h3>
      <p>Your inquiry has been sent to BAi Systems. We’ll reply to arrange a conversation about your project.</p>
      <button type="button" className="ww-text-link" onClick={() => updateStatus('idle')}>Send another inquiry</button>
    </div>
  ) : (
    <form className="ww-inquiry" onSubmit={handleSubmit} aria-label="Wellwired project inquiry" aria-busy={status === 'sending'}>
      <input type="hidden" name="access_key" value="c917a05a-3d9c-4b80-bdbb-ac2c2c4b0a45" />
      <input type="hidden" name="subject" value="Wellwired project inquiry — BAi Systems" />
      <input type="hidden" name="from_name" value="Wellwired by BAi" />
      {reviewVariant && <input type="hidden" name="website_version" value={`Wellwired review ${reviewVariant.toUpperCase()}`} />}
      <input type="checkbox" name="botcheck" className="ww-honeypot" tabIndex="-1" aria-hidden="true" />
      <fieldset disabled={status === 'sending'}>
        <legend className="sr-only">Tell us about your new home</legend>
        <div className="ww-form-row">
          <label htmlFor="ww-name">Name<input id="ww-name" name="name" autoComplete="name" required /></label>
          <label htmlFor="ww-email">Email<input id="ww-email" name="email" type="email" autoComplete="email" required /></label>
        </div>
        <div className="ww-form-row">
          <label htmlFor="ww-role">Your role<select id="ww-role" name="role" defaultValue="" required>
            <option value="" disabled>Select your role</option>
            <option>Homeowner</option><option>Architect</option><option>Interior designer</option>{includeBuildingBiologist && <option>Building biologist</option>}<option>Builder</option><option>Installation partner</option><option>Other</option>
          </select></label>
          <label htmlFor="ww-interest">Start with<select id="ww-interest" name="interest" defaultValue="Free fit call">
            <option>Free fit call</option><option>{includeBuildingBiologist ? 'Design-team conversation' : 'Architect or designer conversation'}</option><option>Consult — {consultPrice}</option><option>Build options</option><option>Verify add-on</option>
          </select></label>
        </div>
        <div className="ww-form-row">
          <label htmlFor="ww-location">Project location<input id="ww-location" name="project_location" placeholder="City, state / country" required /></label>
          <label htmlFor="ww-stage">Build stage<select id="ww-stage" name="build_stage" defaultValue="" required>
            <option value="" disabled>Select the stage</option><option>Exploring a new build</option><option>Schematic design</option><option>Design development</option><option>Construction documents</option><option>Under construction</option><option>Other / not sure</option>
          </select></label>
        </div>
        <label htmlFor="ww-team">{includeBuildingBiologist ? 'Your design team' : 'Architect or designer'} <span>(optional)</span><input id="ww-team" name="design_team" autoComplete="organization" /></label>
        <label htmlFor="ww-timeline">Expected construction timeline <span>(optional)</span><input id="ww-timeline" name="timeline" placeholder="Approximate start and completion" /></label>
        <label htmlFor="ww-message">What matters most for this home?<textarea id="ww-message" name="message" rows="4" placeholder={includeBuildingBiologist ? 'Indoor air quality, low RF or low EMF, lighting, water—or other priorities for the home.' : 'Light, air, radios in the bedrooms, or anything else on your mind.'} required /></label>
        <p className="ww-form-note">For new construction. This sends an inquiry to BAi Systems; we’ll arrange the conversation by email. No appointment or payment is taken here.</p>
        {status === 'error' && <p role="alert" className="ww-form-error">Your inquiry could not be sent. Your details are still here. Please try again, or email <a href="mailto:hello@bai-systems.com">hello@bai-systems.com</a>.</p>}
        <button className="ww-action" type="submit"><span className="ww-crossfade"><span aria-hidden={status === 'sending'}>Send project inquiry</span><span aria-hidden={status !== 'sending'}>Sending…</span></span> <span aria-hidden="true">↗</span></button>
      </fieldset>
    </form>
  );
  return <div className="ww-inquiry-state" ref={shellRef}><div ref={contentRef}>{content}</div></div>;
}
